/**
 * Component: Cart Upsell
 * -----------------------------------------------------------------------------
 * Logic related to cart upsell products.
 *
 * @namespace cartUpsell
 */

import classes from '~/config/classes'
import { on } from '~/helpers/utils'
import Swiper from 'swiper'
import { Navigation, Pagination } from 'swiper/modules'
import { register } from 'swiper/element/bundle'

register()

/**
 * DOM selectors.
 */
const selectors = {
  colorSwatch: '[js-cart-upsell="colorSwatch"]',
  container: '[js-cart-upsell="container"]',
  product: '[js-cart-upsell="product"]',
  submit: '[js-cart-upsell="submit"]',
}

let swatchData = null

/**
 * Create a new cartUpsell object.
 */
export default () => {

  /**
   * Node Elements.
   */
  const nodes = {
    colorSwatch: document.querySelectorAll(selectors.colorSwatch),
    container: document.querySelector(selectors.container),
    product: document.querySelectorAll(selectors.product),
    submit: document.querySelectorAll(selectors.submit),
  }

  /**
   * Callback after successful add to cart.
   */
  async function onSuccess() {
    await fetch('/cart.js')
      .then((response) => response.json())
      .then((data) => {
        document.dispatchEvent(
          new CustomEvent('theme:cart:change', {
            detail: {
              cart: data,
            },
            bubbles: true,
          }),
        )

        window.basis.EventBus.emit('Cart:upsell:added')
        window.basis.EventBus.emit('Cart:render:full')
      })
  }

  /**
   * Add one or more items to cart.
   * @param {Element} button - Button clicked.
   */
  async function handleAddToCart(button) {
    const body = JSON.stringify({
      id: button.dataset.id,
      quantity: 1,
    })

    await fetch('/cart/add.js', {
      body,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'xmlhttprequest',
      },
      method: 'POST',
    })
      .then(() => {
        onSuccess()
        button.innerText = window.basis.strings.cart.addedToCart
        setTimeout(() => {
          button.innerText = window.basis.strings.cart.addToCart
        }, 3000)
      })
      .catch((err) => {
        console.error(err)
      })
  }

  /**
   * Get swatch style from swatchData, hex or .svg.
   * @param {String} color - Color choice.
   * @returns {String}
   */
  function getSwatchStyle(color) {
    const swatch = swatchData.find((obj) => obj.key === color)

    if (!swatch) {
      return ''
    }

    let style = `background-color: ${swatch.value};`

    if (swatch.value.includes('.svg')) {
      const url = `${window.theme.assets.base}${swatch.value}`

      style = `background-image: url(${url});`
    }

    return style
  }

  /**
   * Set swatch backgrounds.
   */
  function setSwatches() {
    nodes.colorSwatch.forEach((swatch) => {
      const color = swatch.dataset.color.toLowerCase()
      const style = getSwatchStyle(color)

      swatch.style = style
    })
  }

  /**
   * Build swatch carousels.
   */
  function buildSwatchCarousels() {
    nodes.product.forEach((card) => {
      const productId = card.dataset.id
      const colorSwatch = card.querySelectorAll(selectors.colorSwatch)

      if (!colorSwatch.length) {
        return
      }

      // eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars
      const swiper = new Swiper(`.cart-drawer__upsell-product-${productId} .swiper`, {
        modules: [Navigation, Pagination],
        slidesPerView: 'auto',
        navigation: {
          nextEl: `.cart-drawer__upsell-product-${productId} .swiper-button-next`,
          prevEl: `.cart-drawer__upsell-product-${productId} .swiper-button-prev`,
        },
      })
    })
  }

  /**
   * Get swatch data and format it for use in component.
   */
  function getSwatches() {
    fetch(theme.assets.swatches)
      .then((response) => response.json())
      .then((data) => {
        const array = Object.freeze(data.colors)

        swatchData = Object.keys(array).map((swatch) => {
          const key = Object.keys(array[swatch])[0]
          const value = Object.values(array[swatch])[0]

          return {
            key,
            value,
          }
        })

        if (!nodes.colorSwatch.length) {
          return
        }

        setSwatches()
        buildSwatchCarousels()
      })
  }

  /**
   * Handle swatch click.
   * @param {Element} swatch - Swatch button clicked.
   */
  function handleSwatchClick(swatch) {
    if (swatch.classList.contains(classes.active)) {
      return
    }

    const { id, image } = swatch.dataset
    const product = swatch.closest(selectors.product)
    const img = product.querySelector('img')
    const swatches = product.querySelectorAll(selectors.colorSwatch)
    const submit = product.querySelector(selectors.submit)

    submit.dataset.id = id
    img.src = image

    swatches.forEach((button) => {
      button.classList.remove(classes.active)
    })

    swatch.classList.add(classes.active)
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {
    nodes.submit.forEach((button) => {
      on('click', button, () => handleAddToCart(button))
    })

    if (nodes.colorSwatch.length) {
      nodes.colorSwatch.forEach((swatch) => {
        on('click', swatch, () => handleSwatchClick(swatch))
      })
    }
  }

  /**
   * Initialize component.
   */
  function init() {
    if (!nodes.container) {
      return
    }

    setEventListeners()
    getSwatches()
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
